import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const CATEGORIES = ["Nos Pizzas Rouges", "Nos Pizzas Blanches", "Calabria Style", "Les Formules Midi", "Desserts", "Boissons"];
const BADGE_OPTIONS = [
  { key: "vegetarien", label: "Végétarienne" },
  { key: "epice", label: "Épicée" },
  { key: "coup_de_coeur", label: "Coup de cœur" },
];

const EMPTY = { name: "", description: "", ingredients: "", price: "", category: CATEGORIES[0], badges: [], image: "", available: true };

export default function PizzaManager() {
  const [pizzas, setPizzas] = useState([]);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/pizzas").then((r) => setPizzas(r.data)).catch(() => toast.error("Chargement impossible"));
  useEffect(() => { load(); }, []);

  const openCreate = () => setForm({ ...EMPTY });
  const openEdit = (p) => setForm({ ...p, ingredients: p.ingredients.join(", "), price: String(p.price) });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      name: form.name.trim(),
      description: form.description.trim(),
      ingredients: form.ingredients.split(",").map((s) => s.trim()).filter(Boolean),
      price: parseFloat(String(form.price).replace(",", ".")),
      category: form.category,
      badges: form.badges,
      image: form.image.trim(),
      available: form.available,
    };
    try {
      if (form.id) {
        await api.put(`/pizzas/${form.id}`, payload);
        toast.success("Pizza mise à jour");
      } else {
        await api.post("/pizzas", payload);
        toast.success("Pizza ajoutée à la carte");
      }
      setForm(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    try {
      await api.delete(`/pizzas/${p.id}`);
      toast.success(`${p.name} retirée de la carte`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleAvailable = async (p) => {
    try {
      await api.put(`/pizzas/${p.id}`, { available: !p.available });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleBadge = (key) =>
    setForm((f) => ({ ...f, badges: f.badges.includes(key) ? f.badges.filter((b) => b !== key) : [...f.badges, key] }));

  return (
    <div>
      <div className="flex items-center justify-between">
        <p className="text-sm text-dim">{pizzas.length} produits à la carte</p>
        <button data-testid="admin-add-pizza-button" onClick={openCreate} className="btn-primary !py-2.5 !px-5 text-sm">
          <Plus size={15} /> Ajouter
        </button>
      </div>

      <div className="mt-6 overflow-hidden rounded-2xl border border-border/60">
        {pizzas.map((p) => (
          <div
            key={p.id}
            data-testid={`admin-pizza-row-${p.id}`}
            className="flex flex-wrap items-center gap-4 border-b border-border/50 bg-surface-2 px-5 py-4 last:border-0"
          >
            {p.image ? (
              <img src={p.image} alt="" className="h-11 w-11 rounded-lg object-cover" />
            ) : (
              <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-surface-3 text-xs font-bold text-ochre">
                {p.name.slice(0, 2).toUpperCase()}
              </span>
            )}
            <div className="min-w-0 flex-1">
              <p className="font-semibold">{p.name} <span className="ml-2 text-xs text-dim">{p.category}</span></p>
              <p className="truncate text-xs text-dim">{p.ingredients.join(" · ")}</p>
            </div>
            <span className="font-display text-sm font-bold text-ochre">{p.price.toFixed(2).replace(".", ",")} €</span>
            <button
              data-testid={`admin-pizza-available-${p.id}`}
              onClick={() => toggleAvailable(p)}
              className={`badge-tag ${p.available ? "badge-veg" : "badge-hot"}`}
            >
              {p.available ? "Disponible" : "Épuisée"}
            </button>
            <button data-testid={`admin-pizza-edit-${p.id}`} onClick={() => openEdit(p)} className="rounded-full border border-border p-2 text-dim hover:border-terra hover:text-terra transition-colors" aria-label="Modifier">
              <Pencil size={14} />
            </button>
            <button data-testid={`admin-pizza-delete-${p.id}`} onClick={() => remove(p)} className="rounded-full border border-border p-2 text-dim hover:border-flame hover:text-flame transition-colors" aria-label="Supprimer">
              <Trash2 size={14} />
            </button>
          </div>
        ))}
        {pizzas.length === 0 && <p className="bg-surface-2 px-5 py-10 text-center text-sm text-dim">La carte est vide.</p>}
      </div>

      <AnimatePresence>
        {form && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-5 backdrop-blur-sm"
            onClick={() => setForm(null)}
          >
            <motion.form
              data-testid="admin-pizza-form"
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 40, opacity: 0 }}
              onSubmit={save}
              onClick={(e) => e.stopPropagation()}
              className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-border bg-surface-2 p-7"
            >
              <div className="flex items-center justify-between">
                <h3 className="font-display text-lg font-bold">{form.id ? "Modifier" : "Nouvelle pizza"}</h3>
                <button type="button" onClick={() => setForm(null)} className="rounded-full border border-border p-2 text-dim hover:text-terra" aria-label="Fermer">
                  <X size={15} />
                </button>
              </div>
              <div className="mt-5 space-y-4">
                <input data-testid="pizza-form-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nom (ex : Margherita)" className="admin-input" />
                <textarea data-testid="pizza-form-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description courte" rows={2} className="admin-input" />
                <input data-testid="pizza-form-ingredients" value={form.ingredients} onChange={(e) => setForm({ ...form, ingredients: e.target.value })} placeholder="Ingrédients, séparés par des virgules" className="admin-input" />
                <div className="grid grid-cols-2 gap-4">
                  <input data-testid="pizza-form-price" required value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="Prix (ex : 12.50)" className="admin-input" />
                  <select data-testid="pizza-form-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="admin-input">
                    {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <input data-testid="pizza-form-image" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} placeholder="URL de l'image (optionnel)" className="admin-input" />
                <div className="flex flex-wrap gap-2">
                  {BADGE_OPTIONS.map((b) => (
                    <button
                      type="button"
                      key={b.key}
                      data-testid={`pizza-form-badge-${b.key}`}
                      onClick={() => toggleBadge(b.key)}
                      className={`badge-tag transition-opacity ${form.badges.includes(b.key) ? "badge-fav ring-1 ring-current" : "border-border text-dim opacity-60"}`}
                    >
                      {b.label}
                    </button>
                  ))}
                </div>
                <label className="flex items-center gap-2.5 text-sm text-dim">
                  <input data-testid="pizza-form-available" type="checkbox" checked={form.available} onChange={(e) => setForm({ ...form, available: e.target.checked })} className="accent-[#E05624]" />
                  Disponible à la vente
                </label>
                <button data-testid="pizza-form-submit" type="submit" disabled={saving} className="btn-primary w-full justify-center disabled:opacity-60">
                  {saving ? "Enregistrement…" : form.id ? "Enregistrer les modifications" : "Ajouter à la carte"}
                </button>
              </div>
            </motion.form>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
